<template>
  <slice
    v-if="currentImageOrVideo"
    :id="block._key"
    class="block--image-or-video-per-color"
    :class="{
      'show-content': showContent,
    }"
    @out="onBlockOutOfWindow"
  >
    <div class="block__media">
      <lazy-image
        v-if="images && images.length > 0"
        :key="`image-${currentImageOrVideo._key}`"
        :images="images"
        provider="sanity"
      />

      <div
        v-if="isVideo"
        class="video-container"
        :class="{
          'd-none d-md-block video-container--desktop':
            currentImageOrVideo.video_mobile &&
            currentImageOrVideo.video_mobile.id,
        }"
        :style="{
          '--video-ratio': videoSize
            ? `calc(${videoSize.height} / ${videoSize.width})`
            : 'calc(5 / 4)',
        }"
      >
        <video-bunny
          :src="`https://iframe.mediadelivery.net/embed/${
            BUNNY_STREAM_FOLDERS[currentImageOrVideo.video.folder]
          }/${
            currentImageOrVideo.video.id
          }?responsive=true&autoplay=true&loop=true&muted=true&preload=true`"
          autoplay
        />
      </div>
      <div
        v-if="
          isVideo &&
          currentImageOrVideo.video_mobile &&
          currentImageOrVideo.video_mobile.id
        "
        class="video-container"
        :class="{
          'd-md-none video-container--mobile': currentImageOrVideo.video_mobile,
        }"
        :style="{
          '--video-ratio': videoMobileSize
            ? `calc(${videoMobileSize.height} / ${videoMobileSize.width})`
            : 'calc(16 / 9)',
        }"
      >
        <video-bunny
          :src="`https://iframe.mediadelivery.net/embed/${
            BUNNY_STREAM_FOLDERS[currentImageOrVideo.video_mobile.folder]
          }/${
            currentImageOrVideo.video_mobile.id
          }?responsive=true&autoplay=true&loop=true&muted=true&preload=true`"
          autoplay
        />
      </div>

      <div
        v-if="showKSP || showBtnAndContent"
        :class="`block__overlay horizontal--${block.horizontal_position} vertical--${block.vertical_position}`"
        @click="overlayClick"
      >
        <div :data-v-sticky-container-image-per-video="block._key">
          <p
            v-if="showKSP"
            :class="`block__ksp text--${block.color}`"
            :style="block.color === 'custom' && { color: block.custom_color }"
          >
            {{ block.ksp }}
          </p>

          <transition-group
            tag="div"
            :css="false"
            @before-enter="switchActionsBeforeEnter"
            @enter="switchActionsEnter"
            @leave="switchActionsLeave"
            @after-enter="switchActionsAfterEnter"
          >
            <div
              v-if="showBtnAndContent && !showContent"
              :key="'show'"
              v-sticky="activateSticky ? sticky : false"
              :data-v-sticky-inner-image-per-video="block._key"
            >
              <primary-button
                :text="block.btn_label"
                with-arrow
                arrow-is-icon
                icon="info"
                size="secondary"
                aspect="background-shadow"
                class="show"
                @click="handleShowContent"
              />
            </div>

            <div v-if="showBtnAndContent && showContent" :key="'hide'">
              <primary-button
                :text="$t('action.close')"
                text-visually-hidden
                with-arrow
                arrow-is-icon
                no-shadow
                aspect="light"
                icon="cross"
                class="close"
              />
            </div>
          </transition-group>
        </div>
      </div>
    </div>

    <div
      v-if="showBtnAndContent && block.content"
      ref="content"
      :class="`block__content vertical--${block.vertical_position}`"
    >
      <portable-text :blocks="block.content" />
    </div>
  </slice>
</template>

<script>
import NuxtSSRScreenSize from 'nuxt-ssr-screen-size'

import Slice from '@/components/Slices/Slice'
import LazyImage from '@/components/Image/LazyImage'
import PrimaryButton from '@/components/buttons/Primary'
import PortableText from '@/components/RichText/PortableText'
import VideoBunny from '@/components/video/bunny'

import { BUNNY_STREAM_FOLDERS } from '@/const'

import { getSanityBunnyImageLink } from '@/utils/image'

export default {
  name: 'ProductImageOrVideoPerColor',
  components: {
    Slice,
    LazyImage,
    VideoBunny,
    PrimaryButton,
    PortableText,
  },
  mixins: [NuxtSSRScreenSize.NuxtSSRScreenSizeMixin],
  props: {
    block: { type: Object, required: true },
    selectedColor: { type: Object, required: true },
    gridFormat: { type: String, default: undefined },
  },
  data() {
    return {
      showContent: false,
      BUNNY_STREAM_FOLDERS,
      activateSticky: true,
      sticky: {
        containerSelector: `[data-v-sticky-container-image-per-video="${this.block._key}"]`,
        innerWrapperSelector: `[data-v-sticky-inner-image-per-video="${this.block._key}"]`,
        resizeSensor: true,
        minWidth: 0,
        topSpacing: 60,
      },
    }
  },
  computed: {
    currentImageOrVideo() {
      return this.block.images.find(
        (image) => image._key === this.selectedColor._key
      )
    },
    isVideo() {
      return this.currentImageOrVideo?._type === 'video'
    },
    videoSize() {
      if (this.isVideo) {
        return {
          width: this.currentImageOrVideo.video.ratio.width,
          height: this.currentImageOrVideo.video.ratio.height,
        }
      }

      return undefined
    },
    videoMobileSize() {
      if (this.isVideo && this.currentImageOrVideo.video_mobile) {
        return {
          width: this.currentImageOrVideo.video_mobile.ratio.width,
          height: this.currentImageOrVideo.video_mobile.ratio.height,
        }
      }

      return undefined
    },
    imageFormat() {
      if (this.block.format) {
        switch (this.block.format) {
          case 'carre':
            return { width: 4, height: 4 }
          case 'landscape':
            return { width: 5, height: 4 }
          default:
            return { width: 4, height: 5 }
        }
      }

      return {
        width: 4,
        height: 5,
      }
    },
    imageMobileFormat() {
      if (this.block.format) {
        switch (this.block.format) {
          case 'carre':
            return { width: 4, height: 4 }
          case 'landscape':
            return { width: 16, height: 9 }
          default:
            return { width: 9, height: 16 }
        }
      }

      return {
        width: 9,
        height: 16,
      }
    },
    images() {
      if (this.isVideo) {
        return undefined
      }

      return [
        {
          image: {
            url: getSanityBunnyImageLink(
              this.currentImageOrVideo.image,
              this.imageMobileFormat
            ),
            dimensions: this.imageMobileFormat,
          },
          widths: [390, 780, 1170, 1560],
          sizes: '100vw',
          media: {
            type: 'max',
            value: 768,
          },
        },
        {
          image: {
            url: getSanityBunnyImageLink(
              this.currentImageOrVideo.image,
              this.imageFormat
            ),
            dimensions: this.imageFormat,
          },
          widths: [390, 780, 1170, 1560, 1950],
          sizes: '100vw',
          media: {
            type: 'min',
            value: 769,
          },
        },
      ]
    },
    showKSP() {
      return this.block.type === 'image-ksp' && this.block.ksp
    },
    showBtnAndContent() {
      return this.block.type === 'image-content' && this.block.btn_label
    },
  },
  methods: {
    async handleShowContent(ev) {
      ev.preventDefault()
      ev.stopPropagation()

      this.showContent = !this.showContent

      if (this.showContent) {
        const { gsap } = await import('gsap' /* webpackChunkName: 'gsap' */)
        const { ScrollToPlugin } = await import(
          'gsap/ScrollToPlugin' /* webpackChunkName: 'gsap-scroll-to' */
        )

        gsap.registerPlugin(ScrollToPlugin)

        this.$nextTick(() => {
          if (this.$refs.content) {
            gsap.to(window, {
              scrollTo: this.$refs.content,
              duration: 0.5,
            })
          }
        })
      }
    },
    overlayClick(ev) {
      ev.preventDefault()
      if (this.showContent) {
        this.showContent = false
      }
    },
    async switchActionsBeforeEnter(el) {
      const { gsap } = await import('gsap' /* webpackChunkName: 'gsap' */)

      el.classList.add('no-animate')
      gsap.set(el, { position: 'absolute', opacity: 0 })
      this.activateSticky = false
    },
    async switchActionsEnter(el, done) {
      const { gsap } = await import('gsap' /* webpackChunkName: 'gsap' */)

      if (el.classList.contains('show')) {
        gsap.fromTo(
          el,
          { opacity: 0, position: 'relative' },
          { opacity: 1, duration: 0.3, delay: 0.5, onComplete: done }
        )
      } else {
        gsap.fromTo(
          el,
          { opacity: 0, position: 'relative', x: '100%' },
          { opacity: 1, x: 0, duration: 0.3, delay: 0.5, onComplete: done }
        )
      }
    },
    switchActionsAfterEnter(el) {
      el.style = ''
      el.classList.remove('no-animate')

      this.$nextTick(() => {
        this.activateSticky = true
      })
    },
    async switchActionsLeave(el, done) {
      const { gsap } = await import('gsap' /* webpackChunkName: 'gsap' */)

      if (el.classList.contains('show')) {
        gsap.fromTo(
          el,
          { opacity: 1 },
          { opacity: 0, duration: 0.3, onComplete: done }
        )
      } else {
        gsap.fromTo(
          el,
          { opacity: 1, x: 0 },
          { opacity: 0, x: 300, duration: 0.3, onComplete: done }
        )
      }
    },
    onBlockOutOfWindow() {
      if (this.showContent) {
        this.showContent = false
      }
    },
  },
}
</script>

<style lang="scss">
.block--image-or-video-per-color {
  position: relative;
  overflow: hidden;

  .block__media {
    position: relative;
    transition: left 0.5s var(--asphalte-animation-function);
    left: 0;
    height: 100%;
    will-change: left;
  }

  .video-container {
    --video-ratio: calc(760 / 390);

    width: 100%;
    pointer-events: none;
    position: relative;
    background-color: black;

    @include mq($until: tablet) {
      height: 0;
      padding-bottom: calc(100vw * var(--video-ratio));
    }

    @include mq($from: tablet) {
      position: absolute;
      height: 0;
      padding-bottom: calc(100% * var(--video-ratio));
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }

  .block__overlay {
    position: absolute;
    inset: 0;
    padding: calc(var(--spacing) * 4) var(--spacing);
    display: flex;
    align-items: stretch;
    z-index: 1;
    transition: background 0.5s var(--asphalte-animation-function);
    will-change: background;

    > div {
      display: flex;
      justify-content: center;
      align-items: center;
      position: relative;
      width: 100%;
    }

    .action {
      .action__text {
        font-size: var(--small-font-size);
        font-weight: 600;
      }
    }

    &.horizontal--left {
      > div {
        justify-content: flex-start;
      }

      .show,
      .close {
        margin-right: auto;
      }
    }

    &.horizontal--right {
      > div {
        justify-content: flex-end;
      }

      .show,
      .close {
        margin-left: auto;
      }
    }

    &.vertical--top {
      > div {
        align-items: flex-start;
      }
    }

    &.vertical--bottom {
      > div {
        align-items: flex-end;
      }
    }

    .close {
      padding: 0;
      width: rem(48px);
      height: rem(48px);

      @include mq($until: tablet) {
        right: calc(var(--spacing) * -1 - 1px);
      }

      @include mq($from: tablet) {
        right: calc(#{rem(460px)} - var(--spacing));
        top: calc(var(--spacing) * -2);
      }

      .action__inner {
        justify-content: center;
      }
    }
  }

  .block__ksp {
    font-family: var(--tertiary-font-family);
    font-size: var(--small-font-size);
    font-optical-sizing: auto;
    font-weight: 500;
    font-style: normal;
    padding: calc(var(--spacing) * 0.6) var(--spacing);
    background-color: var(--tertiary-color);
    backdrop-filter: blur(12.5px);
  }

  .block__content {
    position: absolute;
    z-index: 2;
    padding: calc(var(--spacing) * 2);
    display: flex;
    align-items: center;
    background-color: var(--tertiary-color);
    transition: left 0.5s var(--asphalte-animation-function);
    overflow: auto;
    will-change: left;

    @include mq($until: tablet) {
      width: 85vw;
      top: 0;
      bottom: 0;
      left: 100vw;

      &.vertical--top {
        align-items: flex-start;
      }

      &.vertical--bottom {
        align-items: flex-end;
      }
    }

    @include mq($from: tablet) {
      width: rem(460px);
      right: 0;
      pointer-events: none;
      transform: translateX(100%);
      transition: all 0.5s var(--asphalte-animation-function);
      transition-delay: 0.3s;

      &.vertical--top {
        top: 0;
      }

      &.vertical--bottom {
        bottom: 0;
      }
    }
  }

  &.show-content {
    @include mq($until: tablet) {
      .block__media {
        left: -85vw;
      }

      .block__content {
        left: 15vw;
      }

      .block__overlay {
        background-color: RGB(0 0 0 / 0.2);
      }
    }

    @include mq($from: tablet) {
      .block__content {
        transition-delay: 0s;
        transform: translateX(0);
        pointer-events: auto;
        z-index: 10;
      }
    }
  }
}
</style>
